import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/context/AuthContext';
import { toast } from '@/components/ui/use-toast';

export interface List {
  id: string;
  name: string;
  created_at: string;
  updated_at: string;
  user_id: string;
  position: number;
}

interface ListContextType {
  lists: List[];
  activeListId: string | null;
  setActiveListId: (id: string | null) => void;
  addList: (name: string) => Promise<string | null>;
  updateList: (id: string, name: string) => Promise<void>;
  deleteList: (id: string) => Promise<void>;
  reorderLists: (sourceIndex: number, destinationIndex: number) => Promise<void>;
  loading: boolean;
  fetchLists: () => Promise<void>;
  showCreateListModal: boolean;
  setShowCreateListModal: (show: boolean) => void;
}

const ListContext = createContext<ListContextType | undefined>(undefined);

export const ListProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [lists, setLists] = useState<List[]>([]);
  const [activeListId, setActiveListId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [showCreateListModal, setShowCreateListModal] = useState(false);
  const { user } = useAuth();

  // Save active list ID to localStorage whenever it changes
  useEffect(() => {
    if (user && activeListId) {
      localStorage.setItem(`last_active_list_${user.id}`, activeListId);
    }
  }, [activeListId, user]);

  // Fetch lists when component mounts or user changes
  useEffect(() => {
    if (user) {
      fetchLists();
    } else {
      setLists([]);
      setActiveListId(null);
      setLoading(false);
    }
  }, [user]);

  // Fetch lists from Supabase
  const fetchLists = async () => {
    if (!user) return;

    setLoading(true);
    try {
      console.log('Fetching lists for user:', user.id);
      
      // Order by position first, fall back to created_at if position is null
      const { data, error } = await supabase
        .from('lists')
        .select('*')
        .eq('user_id', user.id)
        .order('position', { ascending: true });

      if (error) {
        console.error('Error fetching lists:', error);
        throw error;
      }

      console.log('Fetched lists:', data);

      // If the user has no lists, create a default list
      if (!data || data.length === 0) {
        console.log('No lists found, creating default list');
        const defaultListId = await addList("My First List");
        if (defaultListId) {
          const defaultList = {
            id: defaultListId,
            name: "My First List",
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
            user_id: user.id,
            position: 0
          };
          setLists([defaultList]);
          setActiveListId(defaultListId);
        }
      } else {
        // Sort by position, handling null values
        const sortedLists = [...data].sort((a, b) => {
          const posA = a.position ?? 0;
          const posB = b.position ?? 0;
          return posA - posB;
        });
        
        setLists(sortedLists);
        
        // Try to restore the last active list from localStorage
        const lastActiveListId = localStorage.getItem(`last_active_list_${user.id}`);
        
        if (lastActiveListId && sortedLists.some(list => list.id === lastActiveListId)) {
          // If the saved list exists in the user's lists, set it as active
          setActiveListId(lastActiveListId);
        } else if (!activeListId && sortedLists.length > 0) {
          // Otherwise, set the first list as active
          setActiveListId(sortedLists[0].id);
        }
      }
    } catch (error) {
      console.error('Error fetching lists:', error);
      toast({
        description: "There was a problem loading your lists."
      });
    } finally {
      setLoading(false);
    }
  };

  // Add a new list
  const addList = async (name: string): Promise<string | null> => {
    if (!user) return null;

    try {
      // Get the highest position for new list
      const highestPosition = lists.length > 0 
        ? Math.max(...lists.map(list => list.position ?? 0)) + 1 
        : 0;

      const newList = {
        name,
        user_id: user.id,
        position: highestPosition
      };

      console.log('Creating new list:', newList);

      const { data, error } = await supabase
        .from('lists')
        .insert(newList)
        .select('*')
        .single();

      if (error) {
        console.error('Error creating list:', error);
        throw error;
      }

      console.log('Created list:', data);

      setLists(prev => [...prev, data]);
      
      // Set new list as active
      setActiveListId(data.id);
      
      toast({
        description: `Your list "${name}" has been created.`
      });
      
      return data.id;
    } catch (error) {
      console.error('Error adding list:', error);
      toast({
        description: "There was a problem creating your list."
      });
      return null;
    }
  };

  // Update a list - only update name and updated_at
  const updateList = async (id: string, name: string): Promise<void> => {
    if (!user) return;

    try {
      const updateTime = new Date().toISOString();
      const { error } = await supabase
        .from('lists')
        .update({ 
          name, 
          updated_at: updateTime 
        })
        .eq('id', id)
        .eq('user_id', user.id);

      if (error) {
        throw error;
      }

      setLists(prev => prev.map(list => 
        list.id === id ? { ...list, name, updated_at: updateTime } : list
      ));
      
      toast({
        description: `Your list has been renamed to "${name}".`
      });
    } catch (error) {
      console.error('Error updating list:', error);
      toast({
        description: "There was a problem updating your list."
      });
    }
  };

  // Delete a list
  const deleteList = async (id: string): Promise<void> => {
    if (!user) return;

    // Don't allow deletion of the last list
    if (lists.length <= 1) {
      toast({
        description: "You must have at least one list."
      });
      return;
    }

    try {
      const { error } = await supabase
        .from('lists')
        .delete()
        .eq('id', id)
        .eq('user_id', user.id);

      if (error) {
        throw error;
      }

      const updatedLists = lists.filter(list => list.id !== id);
      setLists(updatedLists);
      
      // If the active list was deleted, set the first available list as active
      if (activeListId === id && updatedLists.length > 0) {
        setActiveListId(updatedLists[0].id);
      }
      
      toast({
        description: "Your list has been deleted."
      });
    } catch (error) {
      console.error('Error deleting list:', error);
      toast({
        description: "There was a problem deleting your list."
      });
    }
  };

  // Reorder lists with position updates
  const reorderLists = async (sourceIndex: number, destinationIndex: number): Promise<void> => {
    if (!user) return;

    try {
      // Create new array with reordered lists
      const reorderedLists = [...lists];
      const [removed] = reorderedLists.splice(sourceIndex, 1);
      reorderedLists.splice(destinationIndex, 0, removed);

      // Update positions for all lists
      const updatedLists = reorderedLists.map((list, index) => ({
        ...list,
        position: index
      }));

      setLists(updatedLists);

      // Update positions in the database
      const updates = updatedLists.map(list => 
        supabase
          .from('lists')
          .update({ position: list.position })
          .eq('id', list.id)
          .eq('user_id', user.id)
      );

      await Promise.all(updates);
    } catch (error) {
      console.error('Error reordering lists:', error);
      toast({
        description: "There was a problem reordering your lists."
      });
    }
  };

  return (
    <ListContext.Provider
      value={{
        lists,
        activeListId,
        setActiveListId,
        addList,
        updateList,
        deleteList,
        reorderLists,
        loading,
        fetchLists,
        showCreateListModal,
        setShowCreateListModal
      }}
    >
      {children}
    </ListContext.Provider>
  );
};

export const useLists = () => {
  const context = useContext(ListContext);
  if (context === undefined) {
    throw new Error('useLists must be used within a ListProvider');
  }
  return context;
};
